import { Check, Copy } from 'lucide-react'
import * as React from 'react'
import { Button } from './button'
import { type ResultsTableData, ResultsTables } from './results-table'

export type ReportType = 'desk_digest' | 'sla' | 'billing_unposted' | 'customer'

export interface ReportPeriod {
  label: string
  start: string
  end: string
}

export interface ReportSectionData {
  id: string
  title: string
  sensitivity?: 'staff' | 'money'
  summary?: string | null
  bullets?: string[]
  tables?: ResultsTableData[]
}

export interface ReportCardData {
  kind?: 'report'
  report_type: ReportType
  title: string
  generated_at: string
  period: ReportPeriod
  state?: 'complete' | 'partial' | 'unavailable'
  summary: string
  customer_ref?: string | null
  sections: ReportSectionData[]
}

const clean = (value: unknown) =>
  String(value ?? '')
    .replaceAll('|', '\\|')
    .replaceAll('\n', ' ')

export function reportMarkdown(report: ReportCardData): string {
  const lines = [`# ${report.title}`, '', report.period.label, '', report.summary]
  for (const section of report.sections) {
    lines.push('', `## ${section.title}`)
    if (section.summary) lines.push('', section.summary)
    if (section.bullets?.length) lines.push('', ...section.bullets.map((item) => `- ${item}`))
    for (const table of section.tables ?? []) {
      const hasCases = table.rows.some((row) => row.case)
      const headers = [...(hasCases ? ['Case'] : []), ...table.columns.map((column) => column.label)]
      lines.push('', `| ${headers.join(' | ')} |`, `| ${headers.map(() => '---').join(' | ')} |`)
      for (const row of table.rows) {
        const values = [
          ...(hasCases ? [row.case ? `[${row.case.case_number}](${row.case.href})` : ''] : []),
          ...table.columns.map((column) => clean(row.cells[column.key])),
        ]
        lines.push(`| ${values.join(' | ')} |`)
      }
      if (table.next_cursor) lines.push('', '_More results are available._')
    }
  }
  return `${lines.join('\n').trim()}\n`
}

export function ReportCard({ report }: { report: ReportCardData }) {
  const [copied, setCopied] = React.useState(false)
  const state = report.state ?? 'complete'
  const copy = async () => {
    await navigator.clipboard.writeText(reportMarkdown(report))
    setCopied(true)
    globalThis.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <article className="overflow-hidden rounded-lg border border-border bg-card shadow-sm" aria-label={report.title}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border bg-muted/40 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold">{report.title}</h2>
            {state !== 'complete' ? (
              <span className="rounded-full bg-warning-soft px-2 py-0.5 text-xs font-medium text-warning">
                {state === 'partial' ? 'Partial' : 'Unavailable'}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-subtle">{report.period.label}</p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={() => void copy()} aria-label={`Copy ${report.title} as Markdown`}>
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? 'Copied' : 'Copy as Markdown'}
        </Button>
      </header>
      <div className="space-y-4 p-4">
        <p className="text-sm">{report.summary}</p>
        {report.sections.map((section) => (
          <section key={section.id} className="space-y-2" aria-label={section.title}>
            <h3 className="text-sm font-semibold">{section.title}</h3>
            {section.summary ? <p className="text-sm text-subtle">{section.summary}</p> : null}
            {section.bullets?.length ? (
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {section.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
            <ResultsTables tables={section.tables ?? []} />
          </section>
        ))}
      </div>
    </article>
  )
}
